import { useState, useEffect } from "react";
import api, { formatApiError } from "../../lib/api";
import { Button } from "../../components/ui/button";
import { Input } from "../../components/ui/input";
import { Textarea } from "../../components/ui/textarea";
import { toast } from "sonner";
import { Check, Clock, AlertTriangle, Mail, Edit, X } from "lucide-react";
import { useNavigate } from "react-router-dom";
import { AccessBlocked } from "../../components/AccessBlocked";

export default function CRMMyWorkPage() {
  const navigate = useNavigate();
  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(true);
  const [err, setErr] = useState("");
  const [blocked, setBlocked] = useState("");
  const [completing, setCompleting] = useState({});
  const [tab, setTab] = useState("work");
  const [page, setPage] = useState(1);
  const [pageSize, setPageSize] = useState(50);
  const [total, setTotal] = useState(0);
  const [pages, setPages] = useState(1);
  const [showing, setShowing] = useState("");
  const [drafts, setDrafts] = useState([]);
  const [draftsLoading, setDraftsLoading] = useState(false);
  const [editingDraft, setEditingDraft] = useState(null);
  const [editBody, setEditBody] = useState("");

  // FIX-UP (Fix 5a): the endpoint returns {rows, total, page, page_size, pages, showing}
  const load = () => {
    setLoading(true);
    api.get(`/crm/my-work?page=${page}&page_size=${pageSize}`)
      .then(r => {
        const d = r.data || {};
        setItems(d.rows || []);
        setTotal(d.total || 0);
        setPages(d.pages || 1);
        setShowing(d.showing || "");
      })
      .catch(e => {
        // FX3b item 8 — plain English on a 403, never the permission key
        if (e.response?.status === 403) { setBlocked(e.response?.data?.detail || "forbidden"); return; }
        setErr(e.response?.data?.detail || "Failed to load your work list");
      })
      .finally(() => setLoading(false));
  };
  useEffect(load, [page, pageSize]);
  useEffect(() => { setPage(1); }, [pageSize]);

  const handleQuickComplete = async (naId) => {
    const outcome = prompt("Outcome / note:");
    if (outcome === null) return;
    setCompleting(c => ({ ...c, [naId]: true }));
    try {
      await api.post("/crm/next-actions/complete", { next_action_id: naId, outcome, summary: outcome });
      toast.success("Done — next action scheduled");
      load();
    } catch (e) { toast.error(formatApiError(e.response?.data?.detail)); }
    setCompleting(c => ({ ...c, [naId]: false }));
  };

  const loadDrafts = () => {
    setDraftsLoading(true);
    api.get("/crm/drafts?status=pending")
      .then(r => setDrafts(r.data || []))
      .catch(() => {})
      .finally(() => setDraftsLoading(false));
  };
  useEffect(() => { if (tab === "approvals") loadDrafts(); }, [tab]);

  const handleDraftAction = async (draftId, action, edited) => {
    try {
      await api.post(`/crm/drafts/${draftId}/approve`, { action, edited_body: edited || "" });
      toast.success(action === "reject" ? "Draft rejected" : "Draft approved & sent");
      setEditingDraft(null);
      loadDrafts();
    } catch (e) { toast.error(formatApiError(e.response?.data?.detail)); }
  };

  if (blocked) return <div className="p-8"><AccessBlocked detail={blocked} testid="crm-access-blocked" /></div>;
  if (err) return <div className="p-8 text-center"><p className="text-zinc-500 text-sm">{err}</p></div>;

  const overdue = items.filter(i => i.status === "overdue");
  const open = items.filter(i => i.status === "open");

  return (
    <div data-testid="crm-my-work-page">
      <div className="mb-6 flex items-end justify-between flex-wrap gap-2">
        <div>
          <h1 className="font-heading text-2xl font-bold tracking-tight text-zinc-950">My Work</h1>
          <p className="text-sm text-zinc-500" data-testid="my-work-counts">{total} open actions · {overdue.length} overdue on this page</p>
        </div>
        <select value={pageSize} onChange={e => setPageSize(Number(e.target.value))}
          className="h-8 border border-zinc-200 rounded-sm text-xs px-1 bg-white" data-testid="my-work-page-size">
          {[25, 50, 100].map(n => <option key={n} value={n}>{n} per page</option>)}
        </select>
      </div>

      <div className="flex gap-1 border-b border-zinc-200 mb-4">
        <button className={`px-3 py-1.5 text-xs font-medium border-b-2 ${tab === "work" ? "border-zinc-900 text-zinc-900" : "border-transparent text-zinc-400"}`} onClick={() => setTab("work")} data-testid="tab-work">Actions ({total})</button>
        <button className={`px-3 py-1.5 text-xs font-medium border-b-2 ${tab === "approvals" ? "border-zinc-900 text-zinc-900" : "border-transparent text-zinc-400"}`} onClick={() => setTab("approvals")} data-testid="tab-approvals">Approvals ({drafts.length})</button>
      </div>

      {tab === "work" && (loading ? <p className="text-xs text-zinc-400">Loading...</p> : (
        <div className="space-y-6">
          {overdue.length > 0 && (
            <div className="border border-red-200 rounded-sm bg-red-50/50" data-testid="overdue-section">
              <div className="px-4 py-2 border-b border-red-200 flex items-center gap-2">
                <AlertTriangle size={14} className="text-red-600" />
                <h2 className="text-sm font-semibold text-red-800">Overdue ({overdue.length})</h2>
              </div>
              {overdue.map(item => (
                <div key={item._id} className="px-4 py-2.5 border-b border-red-100 last:border-0 flex items-center gap-3" data-testid={`work-item-${item._id}`}>
                  <div className="flex-1 min-w-0 cursor-pointer" onClick={() => navigate(`/crm/party/${item.party_id}`)}>
                    <p className="text-xs font-semibold text-zinc-900">{item.party_name} <span className="text-zinc-400 font-normal">· {item.party_company}</span></p>
                    <p className="text-[10px] text-zinc-500">{item.action_type?.replace(/_/g, " ")} via {item.channel} · Due: <span className="text-red-600 font-semibold">{(item.due_at || "").slice(0, 10)}</span> · {item.party_vertical} / {item.party_stage}</p>
                  </div>
                  <Button size="sm" className="h-7 text-[10px] shrink-0" disabled={completing[item._id]} onClick={() => handleQuickComplete(item._id)} data-testid={`complete-${item._id}`}>
                    <Check size={12} className="mr-1" /> Done
                  </Button>
                </div>
              ))}
            </div>
          )}
          <div className="border border-zinc-200 rounded-sm" data-testid="open-section">
            <div className="px-4 py-2 border-b border-zinc-200 flex items-center gap-2">
              <Clock size={14} className="text-blue-500" />
              <h2 className="text-sm font-semibold text-zinc-700">Open ({open.length})</h2>
            </div>
            {open.length === 0 ? <p className="text-center text-zinc-400 text-xs py-8">All caught up!</p> : open.map(item => (
              <div key={item._id} className="px-4 py-2.5 border-b border-zinc-100 last:border-0 flex items-center gap-3" data-testid={`work-item-${item._id}`}>
                <div className="flex-1 min-w-0 cursor-pointer" onClick={() => navigate(`/crm/party/${item.party_id}`)}>
                  <p className="text-xs font-semibold text-zinc-900">{item.party_name} <span className="text-zinc-400 font-normal">· {item.party_company}</span></p>
                  <p className="text-[10px] text-zinc-500">{item.action_type?.replace(/_/g, " ")} via {item.channel} · Due: {(item.due_at || "").slice(0, 10)} · {item.party_vertical} / {item.party_stage}</p>
                </div>
                <Button size="sm" className="h-7 text-[10px] shrink-0" disabled={completing[item._id]} onClick={() => handleQuickComplete(item._id)} data-testid={`complete-${item._id}`}>
                  <Check size={12} className="mr-1" /> Done
                </Button>
              </div>
            ))}
          </div>
          {/* FIX-UP (Fix 5a) — the paging the server has always needed on screen */}
          <div className="flex items-center justify-between mt-2">
            <p className="text-[11px] text-zinc-500" data-testid="my-work-showing">{showing}</p>
            <div className="flex gap-1 items-center">
              <Button size="sm" variant="outline" className="h-7 text-[11px] rounded-sm" disabled={page <= 1}
                onClick={() => setPage(p => p - 1)} data-testid="my-work-prev-page">Previous</Button>
              <span className="text-[11px] text-zinc-500 px-2" data-testid="my-work-page-label">Page {page} of {pages}</span>
              <Button size="sm" variant="outline" className="h-7 text-[11px] rounded-sm" disabled={page >= pages}
                onClick={() => setPage(p => p + 1)} data-testid="my-work-next-page">Next</Button>
            </div>
          </div>
        </div>
      ))}

      {tab === "approvals" && (
        <div data-testid="approvals-section">
          {draftsLoading ? <p className="text-xs text-zinc-400">Loading...</p> : drafts.length === 0 ? (
            <p className="text-center text-zinc-400 text-xs py-8 border border-dashed border-zinc-200 rounded-sm">No pending drafts to approve.</p>
          ) : drafts.map(d => (
            <div key={d._id} className="border border-zinc-200 rounded-sm mb-3 p-3" data-testid={`draft-${d._id}`}>
              <div className="flex items-center gap-2 mb-2">
                <Mail size={14} className="text-blue-500" />
                <span className="text-xs font-semibold">{d.party_name || "Unknown"}</span>
                <span className="text-[10px] text-zinc-400">· {d.channel} · intent: {d.intent || "—"} · confidence: {Math.round((d.confidence || 0) * 100)}%</span>
              </div>
              {d.inbound_message && (
                <div className="bg-zinc-50 border border-zinc-100 rounded-sm p-2 mb-2">
                  <p className="text-[10px] font-semibold text-zinc-500 mb-0.5">Inbound message:</p>
                  <p className="text-xs text-zinc-700">{d.inbound_message.body?.slice(0, 300)}</p>
                </div>
              )}
              <div className="bg-blue-50 border border-blue-100 rounded-sm p-2 mb-2">
                <p className="text-[10px] font-semibold text-blue-600 mb-0.5">Draft reply:</p>
                {editingDraft === d._id ? (
                  <Textarea value={editBody} onChange={e => setEditBody(e.target.value)} className="text-xs min-h-[60px]" data-testid={`edit-body-${d._id}`} />
                ) : (
                  <p className="text-xs text-zinc-800">{d.draft_body}</p>
                )}
              </div>
              <div className="flex gap-2">
                <Button size="sm" className="h-7 text-[10px] bg-emerald-600 hover:bg-emerald-700" onClick={() => handleDraftAction(d._id, "approve")} data-testid={`approve-${d._id}`}>
                  <Check size={12} className="mr-1" /> Approve & Send
                </Button>
                {editingDraft === d._id ? (
                  <Button size="sm" className="h-7 text-[10px] bg-blue-600 hover:bg-blue-700" onClick={() => handleDraftAction(d._id, "edit_send", editBody)} data-testid={`edit-send-${d._id}`}>
                    <Edit size={12} className="mr-1" /> Send Edited
                  </Button>
                ) : (
                  <Button size="sm" variant="outline" className="h-7 text-[10px]" onClick={() => { setEditingDraft(d._id); setEditBody(d.draft_body); }} data-testid={`edit-btn-${d._id}`}>
                    <Edit size={12} className="mr-1" /> Edit
                  </Button>
                )}
                <Button size="sm" variant="ghost" className="h-7 text-[10px] text-red-500" onClick={() => handleDraftAction(d._id, "reject")} data-testid={`reject-${d._id}`}>
                  <X size={12} className="mr-1" /> Reject
                </Button>
              </div>
            </div>
          ))}
        </div>
      )}
    </div>
  );
}
